import { DestroyRef, Injectable, OnDestroy, inject } from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { BehaviorSubject, Observable, Subject, merge } from "rxjs";

import { ConferenceService } from "./conference.service";
import { DeviceService } from "./device.service";
import { LocalMediaService } from "./local.media.service";
import { MediaRulesService } from "./media.rules.service";
import { debug, debugError } from "./utils";

const MUTED_POLL_MS = 1000;

/**
 * Detects iOS audio-session interruptions (cellular calls, FaceTime, etc.)
 * and exposes reactive state plus an explicit recovery action.
 *
 * Detection signals:
 *  - `mute` event on the live local microphone `MediaStreamTrack` and
 *    `track.muted === true` at attach time. This fires for the Dynamic Island
 *    flow where Safari never loses foreground.
 *  - 1 Hz polling of `track.muted` — some iOS builds flip the property
 *    silently without firing `unmute`.
 *
 * Recovery:
 *  - `attemptRecovery()` probes the mic with a *standalone* `getUserMedia`
 *    (never the pipe). If the probe is live (call ended) it injects that track
 *    and clears the interruption. If still muted (call ongoing) it discards the
 *    probe and re-acquires a video-only stream — the probe's `getUserMedia`
 *    just blacked the live camera — leaving audio muted and the popup up.
 *  - `visibilitychange → visible` and window `focus` invoke `attemptRecovery()`
 *    automatically (e.g. the user backgrounds Safari to hang up, then returns).
 *    We deliberately do NOT listen for the mic `unmute` event: iOS fires it
 *    prematurely (before audio actually flows) and, once we go video-only, there
 *    is no mic track for it to fire on. A pure Dynamic Island hang-up (Safari
 *    never backgrounded) recovers only via the manual "Resume audio" tap.
 *
 * No-op on non-iOS.
 */
@Injectable()
export class AudioInterruptionService implements OnDestroy {
  private local = inject(LocalMediaService);
  private conference = inject(ConferenceService);
  private mediaRules = inject(MediaRulesService);
  private destroyRef = inject(DestroyRef);

  private _isInterrupted$ = new BehaviorSubject<boolean>(false);
  public readonly isInterrupted$: Observable<boolean> =
    this._isInterrupted$.asObservable();

  public get isInterrupted(): boolean {
    return this._isInterrupted$.value;
  }

  private _showOverlay$ = new Subject<void>();
  public readonly showOverlay$: Observable<void> =
    this._showOverlay$.asObservable();

  /** Re-surface the interruption overlay (e.g. user tried to unmute while paused). */
  public requestShowOverlay(): void {
    if (this.isInterrupted) {
      this._showOverlay$.next();
    }
  }

  private currentMicTrack: MediaStreamTrack | null = null;
  private currentMicListeners: {
    onMute: () => void;
  } | null = null;
  private mutedPollInterval: ReturnType<typeof setInterval> | null = null;
  private recovering = false;
  private keepingCameraLive = false;
  /** true while we hold a live video-only stream during an ongoing call */
  private cameraLiveOnCall = false;

  private onFocus = () => {
    debug("audio-interruption:onFocus");
    this.maybeAutoRecover();
  };
  private onVisibilityChange = () => {
    debug("audio-interruption:onVisibilityChange", document.visibilityState);
    if (document.visibilityState === "visible") {
      this.maybeAutoRecover();
    }
  };

  constructor() {
    if (!DeviceService.isiOS) {
      return;
    }

    debug("audio-interruption:created");

    // window.addEventListener("focus", this.onFocus);
    document.addEventListener("visibilitychange", this.onVisibilityChange);

    merge(this.local.localStreamReady$, this.local.localStreamReplaced$)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.rebindMicTrack());

    // Catch a stream that was already ready before this service was created.
    this.rebindMicTrack();
  }

  ngOnDestroy() {
    if (!DeviceService.isiOS) {
      return;
    }

    window.removeEventListener("focus", this.onFocus);
    document.removeEventListener("visibilitychange", this.onVisibilityChange);
    this.detachMicListeners();
    // this.stopMutedPolling();
  }

  /**
   * Recover the local mic without disturbing a live local camera.
   *
   * We probe the mic with a *standalone* `getUserMedia` rather than the old
   * mute → unmute pipe flow. While the phone call still holds the audio session
   * the probe either throws or returns a muted track — in both cases we discard
   * it and leave the camera alone (a pipe-driven re-acquire would mute the live
   * video permanently, https://bugs.webkit.org/show_bug.cgi?id=179363). Only
   * when the probe comes back live do we inject that track into the pipe and
   * publish it.
   *
   * Returns true if the mic recovered (call released the session); false if it
   * is still muted (user hasn't hung up yet) and the popup should stay.
   */
  public async attemptRecovery(): Promise<boolean> {
    if (this.recovering || this.keepingCameraLive) {
      debug("audio-interruption:attemptRecovery skipped — busy");
      return false;
    }
    this.recovering = true;
    debug("audio-interruption:attemptRecovery start");

    let probe: MediaStream | null = null;
    try {
      try {
        probe = await navigator.mediaDevices.getUserMedia(
          this.local.getAudioProbeConstraints()
        );
      } catch (ex) {
        debug("audio-interruption:probe failed — still interrupted", ex);
        return false;
      }

      const probeTrack = probe.getAudioTracks()[0] ?? null;

      // NOTE: iOS reports a freshly-acquired track's `muted` flag asynchronously
      // (it can read `false` transiently, then settle to `true` while the call
      // still holds the session). We do NOT block to let it settle — the real
      // arbiter is the post-inject `recovered` check below, which reads after
      // addAudioTrack's async work has given the flag time to settle, and the
      // camera is restored on every "still interrupted" exit regardless.
      const live =
        !!probeTrack && probeTrack.readyState === "live" && !probeTrack.muted;

      debug(
        "audio-interruption::probe track",
        probeTrack,
        probeTrack?.readyState,
        probeTrack?.muted,
        live
      );

      if (!live) {
        debug("audio-interruption:probe muted — still interrupted");
        probe.getTracks().forEach((t) => t.stop());
        // The call still holds the audio session (audio stays muted, popup
        // stays); the probe just blacked the camera, so bring it back.
        await this.restoreCameraAfterProbe();
        return false;
      }

      // Mic looks live — inject the probed track alongside the live camera.
      try {
         debug("audio-interruption: add audio track");
        await this.local.addAudioTrack(probeTrack);
      } catch (ex) {
        // Attach failed — the race slipped through and the mic is still held by
        // the call. The probe blacked the camera, so restore it before bailing.
        debugError(ex);
        probe.getTracks().forEach((t) => t.stop());
        await this.restoreCameraAfterProbe();
        return false;
      }

      this.conference.refreshRemoteAudio();

      const newTrack =
        this.local.mainStream?.stream?.mediaStream?.getAudioTracks()[0] ?? null;
      const recovered = !!newTrack && !newTrack.muted;
      debug("audio-interruption:attemptRecovery result", { recovered });

      if (recovered) {
        this.setInterrupted(false);
      } else {
        // Injection didn't take — the injected track came back muted (still on
        // the call). Drop it and restore the camera the probe blacked.
        probe.getTracks().forEach((t) => t.stop());
        await this.restoreCameraAfterProbe();
      }
      return recovered;
    } finally {
      this.recovering = false;
    }
  }

  /**
   * The audio probe's `getUserMedia` reconfigures iOS's single capture session
   * and blacks a live camera as a side effect. Bring it back with a video-only
   * re-acquire. Safe to call while `recovering` is held: it talks to
   * LocalMediaService directly rather than via maybeKeepCameraLive(), which
   * bails while `recovering`/`keepingCameraLive` is set. No-op when we weren't
   * holding a live camera (audio-only call) or video is disabled in settings.
   */
  private async restoreCameraAfterProbe() {
     debug("audio-interruption: restoring camera after probe before guard");
    if (!this.cameraLiveOnCall || !this.mediaRules.isVideoAvailable) {
      return;
    }
    debug("audio-interruption:restoring camera after probe");
    try {
      await this.local.reacquireVideoOnly();
      this.cameraLiveOnCall = true;
    } catch (ex) {
      debugError(ex);
    }
  }

  private maybeAutoRecover() {
    // Auto call-end detection: focus / visibilitychange fire attemptRecovery
    // even while we hold a live video-only camera during a call. The probe
    // either comes back live (call ended → inject audio, recover) or muted
    // (still on the call → re-acquire the camera the probe just blacked). We do
    // NOT suppress on `cameraLiveOnCall` — that suppression is what broke the
    // auto-detect; the probe's strict live check guards against false positives.
    if (!this.isInterrupted || this.recovering || this.keepingCameraLive) {
      return;
    }
    debug("audio-interruption:auto-recover triggered");
    this.attemptRecovery().catch(debugError);
  }

  /**
   * A phone call interrupts the single iOS capture session, so the joined
   * audio+video stream comes back with BOTH tracks muted (the video is black).
   * getUserMedia doesn't throw, so the room's fallback chain never fires. Here
   * we re-acquire a *video-only* stream — which needs no audio session and so
   * stays live during the call — to keep the local camera visible. Audio is
   * recovered separately by attemptRecovery() once the call ends.
   *
   * We do NOT gate on the video track's own `muted` flag: iOS reports it
   * asynchronously (it lags the actual black-out), so right after a probe — or
   * when the mic re-mutes moments after a "successful" recovery — the video can
   * still read `muted === false` while the camera is in fact black. We only get
   * here while the mic is interrupted, which on iOS means the shared capture
   * session is contended; whenever we have a camera, reacquire video-only.
   */
  private async maybeKeepCameraLive() {
    debug(
      "audio-interruption:maybe keep camera live",
      this.keepingCameraLive,
      this.recovering
    );
    if (this.keepingCameraLive || this.recovering) {
      return;
    }

    const videoTrack =
      this.local.mainStream?.stream?.mediaStream?.getVideoTracks()[0] ?? null;

    if (!videoTrack || !this.mediaRules.isVideoAvailable) {
      return;
    }

    this.keepingCameraLive = true;
    debug("audio-interruption:keep camera live — reacquiring video-only");
    try {
      await this.local.reacquireVideoOnly();
      // We now hold a live video-only stream; auto-recovery stays suppressed
      // until the user taps "Resume audio" or audio actually recovers.
      this.cameraLiveOnCall = true;
    } catch (ex) {
      debugError(ex);
    } finally {
      this.keepingCameraLive = false;
    }
  }

  private setInterrupted(value: boolean) {
    if (this._isInterrupted$.value !== value) {
      debug("audio-interruption:setInterrupted", value);
      this._isInterrupted$.next(value);
    }
    if (!value) {
      // audio is back (or we're no longer interrupted) — leave camera-live mode
      this.cameraLiveOnCall = false;
    }
  }

  private rebindMicTrack() {
    debug("audio-interruption:rebindMicTrack");
    const track =
      this.local.mainStream?.stream?.mediaStream?.getAudioTracks()[0] ?? null;

    if (track === this.currentMicTrack) {
      return;
    }

    this.detachMicListeners();
    this.currentMicTrack = track;

    if (!track) {
      return;
    }

    if (track.muted) {
      debug("audio-interruption: track.muted at attach time");
      this.setInterrupted(true);
      this.maybeKeepCameraLive();
      // this.startMutedPolling();
    }

    const onMute = () => {
      debug("audio-interruption: track mute event");
      this.setInterrupted(true);
      this.maybeKeepCameraLive();
      // this.startMutedPolling();
    };

    // We intentionally do NOT listen for `unmute` to trigger recovery: iOS
    // fires it prematurely (before audio actually flows), and once we go
    // video-only to keep the camera there is no mic track for it to fire on.
    // Auto call-end detection is handled by focus / visibilitychange, and the
    // manual "Resume audio" tap covers a pure Dynamic Island hang-up.
    track.addEventListener("mute", onMute);
    this.currentMicListeners = { onMute };
  }

  private detachMicListeners() {
    if (this.currentMicTrack && this.currentMicListeners) {
      this.currentMicTrack.removeEventListener(
        "mute",
        this.currentMicListeners.onMute
      );
    }
    this.currentMicTrack = null;
    this.currentMicListeners = null;
    // this.stopMutedPolling();
  }

  // private startMutedPolling() {
  //   if (this.mutedPollInterval != null) {
  //     return;
  //   }
  //   debug("audio-interruption:startMutedPolling");
  //   this.mutedPollInterval = setInterval(() => {
  //     if (!this.currentMicTrack) {
  //       this.stopMutedPolling();
  //       return;
  //     }
  //     if (!this.currentMicTrack.muted && this.isInterrupted) {
  //       debug("audio-interruption:poll detected track unmute");
  //       this.setInterrupted(false);
  //     }
  //   }, MUTED_POLL_MS);
  // }

  // private stopMutedPolling() {
  //   if (this.mutedPollInterval != null) {
  //     debug("audio-interruption:stopMutedPolling");
  //     clearInterval(this.mutedPollInterval);
  //     this.mutedPollInterval = null;
  //   }
  // }
}
